import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { Combobox } from '@headlessui/react';

// Función para recuperar borrador (ejecutada ANTES de inicializar estados)
const getInitialFormState = () => {
  try {
    const fromStorage = localStorage.getItem('createUserFormDraft') || sessionStorage.getItem('createUserFormDraft');
    console.log('[CreateUserForm] Datos en storage al montar:', fromStorage);
    if (fromStorage) {
      const draft = JSON.parse(fromStorage);
      console.log('[CreateUserForm] Restaurando draft inicial:', draft);
      return {
        name: draft.name || '',
        email: draft.email || '',
        role: draft.role || '',
        clinicId: draft.clinicId || ''
      };
    }
  } catch (e) {
    console.error('[CreateUserForm] Error restaurando:', e);
  }
  return { name: '', email: '', role: '', clinicId: '' };
};

export default function CreateUserForm({ clinics, onClose, onCreated, onClinicCreated }) {
  const navigate = useNavigate();
  const initialState = getInitialFormState();
  
  const [name, setName] = useState(initialState.name);
  const [email, setEmail] = useState(initialState.email);
  const [role, setRole] = useState(initialState.role);
  const [clinicId, setClinicId] = useState(initialState.clinicId);
  const [showCreateClinic, setShowCreateClinic] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [localClinics, setLocalClinics] = useState(clinics || []);
  const [searchClinic, setSearchClinic] = useState('');

  // Autosave del borrador ante cualquier cambio (localStorage para sobrevivir refresh total)
  useEffect(() => {
    try {
      const payload = JSON.stringify({
        name, email, role, clinicId
      });
      console.log('[CreateUserForm] Guardando borrador:', payload);
      localStorage.setItem('createUserFormDraft', payload);
      sessionStorage.setItem('createUserFormDraft', payload);
    } catch {}
  }, [name, email, role, clinicId]);

  // Sincronizar con prop clinics cuando cambie
  useEffect(() => {
    setLocalClinics(clinics || []);
  }, [clinics]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      // Contraseña temporal: Lucvan2025
      const res = await apiFetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          name,
          email, 
          password: 'Lucvan2025', // Contraseña temporal
          role, 
          clinic_id: clinicId || null 
        }),
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Error creando usuario');
      }
      
      const user = await res.json();
      alert(`Usuario creado exitosamente.\nContraseña temporal: Lucvan2025\nEl usuario debe cambiarla en su primer inicio de sesión.`);
      
      // Limpiar borrador SOLO después de éxito
      try {
        localStorage.removeItem('createUserFormDraft');
        sessionStorage.removeItem('createUserFormDraft');
      } catch {}
      
      if (onCreated) onCreated(user);
      handleClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setEmail('');
    setRole('');
    setClinicId('');
    setShowCreateClinic(false);
    setError('');
    setLoading(false);
    setSearchClinic('');
    try { localStorage.removeItem('createUserFormDraft'); sessionStorage.removeItem('createUserFormDraft'); } catch {}
    if (onClose) onClose();
  };

  // Filtrar clínicas por búsqueda
  const filteredClinics = localClinics.filter(c => 
    c.name.toLowerCase().includes(searchClinic.toLowerCase())
  );

  return (
    <div>
      {showCreateClinic ? (
        <div className="mb-4 p-4 border rounded bg-gray-50">
          <CreateClinicForm 
            onCreated={clinic => {
              setShowCreateClinic(false);
              // Restaurar borrador si existe
              try {
                const raw = localStorage.getItem('createUserFormDraft') || sessionStorage.getItem('createUserFormDraft');
                if (raw) {
                  const draft = JSON.parse(raw);
                  setName(draft.name || '');
                  setEmail(draft.email || '');
                  setRole(draft.role || '');
                  setClinicId(draft.clinicId || '');
                  // Nota: NO limpiamos el borrador aquí para soportar re-mounts del modal
                }
              } catch {}
              setClinicId(clinic.id);
              setLocalClinics([...localClinics, clinic]);
              if (onClinicCreated) onClinicCreated(clinic);
            }}
            onCancel={() => {
              setShowCreateClinic(false);
              try {
                const raw = localStorage.getItem('createUserFormDraft') || sessionStorage.getItem('createUserFormDraft');
                if (raw) {
                  const draft = JSON.parse(raw);
                  setName(draft.name || '');
                  setEmail(draft.email || '');
                  setRole(draft.role || '');
                  setClinicId(draft.clinicId || '');
                  // Mantener borrador hasta que el usuario cree o cierre el formulario principal
                }
              } catch {}
            }}
          />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
          <div>
            <label className="block mb-1 font-medium">Nombre completo</label>
            <input 
              type="text" 
              className="w-full border rounded px-3 py-2" 
              value={name} 
              onChange={e=>{setName(e.target.value); e.target.setCustomValidity('')}} 
              onInvalid={e=>e.target.setCustomValidity('Por favor ingrese el nombre completo')}
              required 
              placeholder="Ej: Juan Pérez" 
            />
          </div>
          <div>
            <label className="block mb-1 font-medium">Email</label>
            <input 
              type="email" 
              className="w-full border rounded px-3 py-2" 
              value={email} 
              onChange={e=>{setEmail(e.target.value); e.target.setCustomValidity('')}} 
              onInvalid={e=>e.target.setCustomValidity('Por favor ingrese un email válido')}
              required 
              placeholder="ejemplo@correo.com" 
            />
          </div>
          <div>
            <label className="block mb-1 font-medium">Rol</label>
            <select 
              className="w-full border rounded px-3 py-2" 
              value={role} 
              onChange={e=>{setRole(e.target.value); e.target.setCustomValidity('')}} 
              onInvalid={e=>e.target.setCustomValidity('Por favor seleccione un rol')}
              required 
            >
              <option value="">Selecciona un rol</option>
              <option value="admin">Administrador</option>
              <option value="clinic">Clínica</option>
              <option value="workshop">Producción</option>
            </select>
          </div>
          {role === 'clinic' && (
            <div>
              <label className="block mb-1 font-medium">Clínica <span className="text-red-600">*</span></label>
              <div className="flex gap-2 items-start">
                <div className="flex-1">
                  <Combobox value={clinicId} onChange={setClinicId}>
                    <div className="relative">
                      <Combobox.Input
                        className="w-full border rounded px-3 py-2"
                        displayValue={(id) => {
                          const found = localClinics.find(c => String(c.id) === String(id));
                          return found ? found.name : '';
                        }}
                        onChange={(e) => setSearchClinic(e.target.value)}
                        placeholder="Buscar y seleccionar clínica..."
                        required
                      />
                      <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {filteredClinics.length === 0 && (
                          <div className="px-3 py-2 text-gray-500">Sin resultados</div>
                        )}
                        {filteredClinics.map((c) => (
                          <Combobox.Option
                            key={c.id}
                            value={String(c.id)}
                            className={({ active }) => `cursor-pointer select-none px-3 py-2 ${active ? 'bg-emerald-600 text-white' : 'text-gray-900'}`}
                          >
                            {c.name}
                          </Combobox.Option>
                        ))}
                      </Combobox.Options>
                    </div>
                  </Combobox>
                </div>
                <button
                  type="button"
                  className="px-3 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 transition"
                  onClick={() => {
                    // Guardar borrador y navegar a crear clínica
                    try {
                      const payload = JSON.stringify({ name, email, role, clinicId });
                      localStorage.setItem('createUserFormDraft', payload);
                      sessionStorage.setItem('createUserFormDraft', payload);
                    } catch {}
                    navigate('/admin/clinics/new?returnTo=/admin/users/new');
                  }}
                >
                  + Nueva clínica
                </button>
              </div>
            </div>
          )}
          <div className="bg-gray-50 border border-gray-200 rounded p-3">
            <p className="text-sm text-gray-600 italic">
              <strong>Nota:</strong> Se creará el usuario con la contraseña temporal: <strong>Lucvan2025</strong>. El usuario deberá cambiarla en su primer inicio de sesión.
            </p>
          </div>
          {error && <div className="text-red-600 text-sm">{error}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" className="px-4 py-2 bg-gray-200 rounded" onClick={handleClose}>Cancelar</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition" disabled={loading}>
              {loading ? 'Creando...' : 'Crear usuario'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// Importar CreateClinicForm del otro archivo
import { CreateClinicForm } from './UserInviteForm';
